import React, { useMemo } from 'react'
import { Modal } from 'semantic-ui-react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { useSubscriptionUsage } from 'context/subscription-usage'
import {
    ACCOUNT_CHECKOUT_PATH,
    ENABLE_FREE_PLAN_USAGE_UI,
    ENABLE_LICENSE_SHOPPING_CART,
} from 'utility/subscription-usage'
import { PLAN_LIMIT_CODE } from 'utility/plan-limit-events'

const Dialog = styled.div`
    .hd {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 12px;
        flex-wrap: wrap;
    }
    .hd h3 {
        font-size: 17px;
        font-weight: 700;
        color: #111827;
        flex: 1;
        margin: 0;
        min-width: 0;
    }
    .badge {
        background: #fef2f2;
        color: #b91c1c;
        font-size: 10px;
        font-weight: 700;
        padding: 3px 8px;
        border-radius: 999px;
        text-transform: uppercase;
        letter-spacing: 0.06em;
    }
    .msg {
        font-size: 13px;
        line-height: 1.6;
        color: #374151;
        margin-bottom: 16px;
    }
    .strip {
        display: flex;
        gap: 12px;
        margin-bottom: 20px;
    }
    .strip .cell {
        flex: 1;
        padding: 10px 14px;
        background: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 7px;
        min-width: 0;
    }
    .strip .k {
        font-size: 10px;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }
    .strip .v {
        font-size: 16px;
        font-weight: 700;
        color: #111827;
        margin-top: 4px;
        font-variant-numeric: tabular-nums;
    }
    .strip .v.over {
        color: #b91c1c;
    }
    .actions {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        flex-wrap: wrap;
    }
    .ghost {
        background: transparent;
        border: none;
        color: #2563eb;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        padding: 8px 10px;
    }
    @media (max-width: 930px) {
        .strip {
            flex-direction: column;
        }
        .actions {
            flex-direction: column-reverse;
            align-items: stretch;
        }
        .actions button,
        .actions .ghost {
            width: 100%;
            text-align: center;
        }
    }
`

export const PlanLimitBlockDialog = () => {
    const navigate = useNavigate()
    const { blockPayload, clearBlockDialog, usage } = useSubscriptionUsage()
    const open = ENABLE_FREE_PLAN_USAGE_UI && Boolean(blockPayload)

    const view = useMemo(() => {
        const code = blockPayload?.code
        const isTemplate = code === PLAN_LIMIT_CODE.TEMPLATE
        const current =
            blockPayload?.currentUsage ??
            (isTemplate
                ? usage?.activeTemplates?.current
                : usage?.seats?.current)
        const cap =
            blockPayload?.cap ??
            (isTemplate ? usage?.activeTemplates?.cap : usage?.seats?.cap)
        const planName = blockPayload?.planName || usage?.planName || 'Free'
        const defaultMessage = isTemplate
            ? `Your Free plan supports up to ${
                  cap ?? 5
              } active templates. You currently have ${current ?? '—'} of ${
                  cap ?? 5
              } active templates. Deactivate an existing template or upgrade your eAuditor plan to activate additional templates.`
            : `Your Free plan supports up to ${
                  cap ?? 10
              } seats. You currently have ${current ?? '—'} of ${
                  cap ?? 10
              } seats in use. Upgrade your eAuditor plan to add more users.`

        return {
            isTemplate,
            badge: isTemplate ? 'Templates' : 'Seats',
            message: blockPayload?.message || defaultMessage,
            usageLabel:
                current != null && cap != null ? `${current} / ${cap}` : '—',
            planName,
            managePath: isTemplate ? '/app/templates' : '/app/settings/user',
            manageLabel: isTemplate ? 'Manage Templates' : 'Manage Users',
        }
    }, [blockPayload, usage])

    if (!open) return null

    const handleUpgrade = () => {
        clearBlockDialog()
        navigate(ACCOUNT_CHECKOUT_PATH)
    }

    const handleManage = () => {
        clearBlockDialog()
        navigate(view.managePath)
    }

    return (
        <Modal open={open} size="small" onClose={clearBlockDialog}>
            <Modal.Content>
                <Dialog>
                    <div className="hd">
                        <h3>Free plan limit reached</h3>
                        <span className="badge">{view.badge}</span>
                    </div>
                    <p className="msg">{view.message}</p>
                    <div className="strip">
                        <div className="cell">
                            <div className="k">{view.badge}</div>
                            <div className="v over">{view.usageLabel}</div>
                        </div>
                        <div className="cell">
                            <div className="k">Plan</div>
                            <div className="v">{view.planName}</div>
                        </div>
                    </div>
                    <div className="actions">
                        <button
                            type="button"
                            className="ghost"
                            onClick={clearBlockDialog}
                        >
                            Not now
                        </button>
                        <AppSecondaryButton
                            type="button"
                            onClick={handleManage}
                        >
                            {view.manageLabel}
                        </AppSecondaryButton>
                        {ENABLE_LICENSE_SHOPPING_CART && (
                            <AppPrimaryButton
                                type="button"
                                onClick={handleUpgrade}
                            >
                                Upgrade Plan
                            </AppPrimaryButton>
                        )}
                    </div>
                </Dialog>
            </Modal.Content>
        </Modal>
    )
}
